import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, BookOpen, ArrowRight, Lock, Award } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const LEVEL_LABELS = { membros: "Membros", coordenadores: "Coordenadores", direcao: "Direção" };

export default function School() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get("/courses", { params: { lang } }).then((r) => setCourses(r.data)).finally(() => setLoading(false));
    api.get("/me/courses/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, [user, lang]);

  return (
    <div>
      <section className="relative bg-th-blue text-white overflow-hidden">
        <div className="th-hero-glow absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <GraduationCap className="w-10 h-10 text-th-gold mb-3" />
          <h1 className="font-display font-bold text-4xl sm:text-5xl">{t("school_title")}</h1>
          <p className="mt-3 text-white/75 max-w-2xl text-lg">{t("school_subtitle")}</p>
          <p className="mt-4 text-sm text-th-goldlight">A formação é reservada aos membros e adaptada ao cargo de cada um.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {!user ? (
          <div className="max-w-lg mx-auto text-center bg-white rounded-2xl border border-slate-200 p-10 shadow-sm" data-testid="school-login-gate">
            <div className="w-14 h-14 rounded-full bg-th-blue/10 flex items-center justify-center mx-auto"><Lock className="w-6 h-6 text-th-blue" /></div>
            <h2 className="mt-4 font-display font-semibold text-xl text-th-blue">Formação reservada a membros</h2>
            <p className="mt-2 text-slate-500">Para aceder à Escola precisa de ter conta e sessão iniciada. Os conteúdos são organizados por nível de acesso conforme o cargo.</p>
            <div className="mt-6 flex gap-3 justify-center">
              <Button asChild className="bg-th-blue text-white hover:bg-th-bluelight" data-testid="school-login-btn"><Link to="/entrar">{t("nav_login")}</Link></Button>
              <Button asChild variant="outline" className="border-slate-300 text-th-blue" data-testid="school-join-btn"><Link to="/adesao">Quero participar</Link></Button>
            </div>
          </div>
        ) : loading ? (
          <p className="text-slate-500">{t("loading")}</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="courses-grid">
            {courses.map((c) => {
              const total = c.modules?.length || 0;
              const done = (progress[c.id] || []).length;
              const pct = total ? Math.round((done / total) * 100) : 0;
              const locked = c.locked;
              return (
                <Link to={`/escola/${c.id}`} key={c.id} data-testid={`course-card-${c.id}`} className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:shadow-xl transition-all flex flex-col">
                  <div className="h-40 overflow-hidden relative">
                    <img src={c.image_url} alt={c.title} className={`w-full h-full object-cover transition-transform duration-500 ${locked ? "grayscale opacity-70" : "group-hover:scale-105"}`} />
                    <Badge className="absolute top-3 left-3 bg-th-gold text-th-bluedark">{LEVEL_LABELS[c.access_level] || c.level}</Badge>
                    {locked && <div className="absolute inset-0 bg-th-blue/45 flex items-center justify-center"><Lock className="w-8 h-8 text-white" data-testid={`course-lock-${c.id}`} /></div>}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-display font-semibold text-xl text-th-blue leading-snug">{c.title}</h3>
                    <p className="mt-2 text-sm text-slate-500 flex-1">{c.description}</p>
                    <div className="mt-3 text-xs text-slate-400 flex items-center gap-1"><BookOpen className="w-3 h-3" />{total} {t("school_modules")}</div>
                    {locked ? (
                      <div className="mt-4 flex items-center text-slate-400 font-medium text-sm"><Lock className="mr-1.5 w-4 h-4" />Reservado a {LEVEL_LABELS[c.access_level]}</div>
                    ) : (
                      <>
                        {total > 0 && (
                          <div className="mt-3">
                            <div className="flex justify-between text-xs text-slate-500 mb-1"><span>{t("school_progress")}</span><span>{pct}%</span></div>
                            <Progress value={pct} className="h-1.5" />
                          </div>
                        )}
                        {pct === 100 ? (
                          <div className="mt-4 flex items-center text-th-gold font-semibold text-sm" data-testid={`course-done-${c.id}`}><Award className="mr-1.5 w-4 h-4" />Concluído · ver certificado</div>
                        ) : (
                          <div className="mt-4 flex items-center text-th-blue font-medium text-sm group-hover:text-th-gold">{t("school_start")} <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
                        )}
                      </>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
